import { CalendarToday, PersonAdd, AttachMoney, Add, RestartAlt } from "@mui/icons-material";
import { Box, Button, IconButton, Stack, Typography, Tooltip, useMediaQuery } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  DateCalendar,
  LocalizationProvider,
  MobileDatePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// isMobile de react-device-detect substituído por useMediaQuery para consistência com MUI
// import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import { useContractContext } from "../../contexts/ContractContext";
import { useLocationContext } from "../../contexts/LocationContext";
import { usePractitionerRoleContext } from "../../contexts/PractitionerRoleContext";
import { useSessionContext } from "../../contexts/SessionContext";
import { CreateAppointmentDialog } from "../../pages/schedulemanager/components/creatappointmentdialog";
import { DateFilterType } from "../../utils/interfaces";
import { useFhirData } from "../../utils/useFhirData";
import { DashViewAllButton } from "../../pages/home/components/PatientDash/components/DashViewAllButton";
import { SecondaryButton } from "../common";
import { CommonDialog } from "../common/CommonDialog";
import { DataSkeleton } from "../common/DataSkeleton";
import { NoDataWarning } from "../common/NoDataWarning";
import VideoConferencia from "../videoConferencia/VideoConferencia";
import { AppointmentDetails } from "./AppointmentDetails";
import { CardSchedule } from "./CardSchedule";
import DateFilter from "./DateFilter";
import { ScheduleCardsMobile } from "./ScheduleCardsMobile";
import { ScheduleSliderDotsBalloon } from "./ScheduleSliderDotsBalloon";
import { marketplaceService } from "@/services/marketplace/MarketplaceService";

interface IProps {
  bundle: any | undefined;
  hasActiveSchedule?: any;
  relatedPatient?: any;
  dateFilter: Dayjs;
  setDateFilter: Dispatch<SetStateAction<Dayjs>>;
  filterType: DateFilterType;
  setFilterType: Dispatch<SetStateAction<DateFilterType>>;
  setRefetch: Dispatch<SetStateAction<boolean>>;
  refetch: boolean;
  loading: boolean;
  /** Layout enxuto para uso dentro do MobileSection do PatientDash */
  compact?: boolean;
  /** Mobile: slider horizontal (padrão) ou lista vertical agrupada por data */
  listLayout?: "slider" | "vertical";
  /** Dash mobile: CTA de agendamento fica no topo da tela */
  hideNewAppointmentButton?: boolean;
  /** Oculta toolbar mobile interna (mês fica no header do dash) */
  hideMobileHeader?: boolean;
  /** Preview do dash: só cards + Ver tudo abaixo do carrossel */
  dashboardPreview?: boolean;
  showViewAllBelow?: boolean;
  onViewAll?: () => void;
}

/**
 * Id do participant pelo TIPO da referência (Patient / Location), não pela posição.
 * Appointments com vários Practitioner/HealthcareService (teleconsultoria, pronto atendimento)
 * não garantem a ordem [Patient, Location, ...] no array de participant.
 * Prefixo sem "/" para aceitar também "Patient?identifier=..." (mesmo split de antes).
 */
function getParticipantId(
  appointment: fhir5.Appointment | undefined,
  type: "Patient" | "Location",
): string | undefined {
  return appointment?.participant
    ?.find((participant: fhir5.AppointmentParticipant) =>
      participant?.actor?.reference?.startsWith(type),
    )
    ?.actor?.reference?.split("/")[1];
}

export function Schedule({
  hasActiveSchedule,
  bundle,
  relatedPatient,
  dateFilter,
  filterType,
  setDateFilter,
  setFilterType,
  loading,
  setRefetch,
  refetch,
  compact = false,
  listLayout = "slider",
  hideNewAppointmentButton = false,
  hideMobileHeader = false,
  dashboardPreview = false,
  showViewAllBelow = false,
  onViewAll,
}: IProps) {
  const [createAppointmentDialogOpen, setCreateAppointmentDialogOpen] =
    useState<boolean>(false);
  const { access, userPatient } = useSessionContext();
  // Detecta tela pequena via MUI para layout responsivo consistente
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { userPractitioner } = usePractitionerRoleContext();
  const { location, setIdsOrganizations } = useLocationContext();
  const { currentContract } = useContractContext();
  const { extractAppointmentData } = useFhirData;

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [openDatePickerMobile, setOpenDatePickerMobile] = useState(false);
  const [dateFieldValue, setDateFieldValue] = useState(dayjs());
  const [selectedCard, setSelectedCard] = useState(0);
  const [isWalkin, setIsWalkin] = useState(false);
  const [isShortCutAppointment, setIsShortCutAppointment] = useState(false);
  const navigate = useNavigate();

  const [openAppointmentDetails, setOpenAppointmentDetails] = useState(false);
  const [hasPaymentLinks, setHasPaymentLinks] = useState<{
    [key: string]: string;
  }>({});
  // Slots já verificados (ou em verificação). Ref, e não estado: marcar um slot
  // não precisa re-renderizar e não pode re-disparar o efeito de pagamento.
  const checkedSlotsRef = useRef<{ [key: string]: boolean }>({});

  /** Slider mobile — ref para sincronizar scroll com card selecionado */
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const isSliderScrolling = useRef(false);

  /** true quando o mês exibido no header mobile já é o mês corrente */
  const isCurrentMonth = dateFieldValue.isSame(dayjs(), "month");

  /** Volta o filtro da agenda para o mês atual (paciente sempre em visão mensal) */
  const handleResetToCurrentMonth = () => {
    const now = dayjs();
    setDateFieldValue(now);
    setDateFilter(now);
    if (access?.type !== "professional") {
      setFilterType("month");
    }
  };

  const isAppointmentBundleEntry = (
    entry: fhir5.BundleEntry<fhir5.Appointment | fhir5.Patient | fhir5.Location>
  ): entry is fhir5.BundleEntry<fhir5.Appointment> =>
    entry.resource?.resourceType === "Appointment";

  const isPatientBundleEntry = (entry: any): any => {
    return entry.resource?.resourceType === "Patient";
  };

  const isLocationBundleEntry = (
    entry: fhir5.BundleEntry<fhir5.Appointment | fhir5.Patient | fhir5.Location>
  ): entry is fhir5.BundleEntry<fhir5.Location> =>
    entry.resource?.resourceType === "Location";

  const patientsBundle = bundle?.entry?.filter(isPatientBundleEntry);

  const locationsBundle = bundle?.entry?.filter(isLocationBundleEntry);

  const appointmentsBundle = bundle?.entry
    ?.filter(isAppointmentBundleEntry)
    ?.filter((el: any) =>
      access?.type === "professional"
        ? true
        : el.resource?.end && new Date(el.resource?.end) > new Date(Date.now())
    );

  /** Consultas visíveis no período selecionado (evita stale data de outro mês) */
  const activePeriodDate =
    isMobile && !hideMobileHeader ? dateFieldValue : dateFilter;
  const visibleAppointments = (appointmentsBundle ?? []).filter((el: any) => {
    const start = el?.resource?.start;
    if (!start) return false;
    const appointmentDate = dayjs(start);
    if (filterType === "day") return appointmentDate.isSame(activePeriodDate, "day");
    if (filterType === "week") return appointmentDate.isSame(activePeriodDate, "week");
    return appointmentDate.isSame(activePeriodDate, "month");
  });

  /** Mesma regra da listagem de cards — header e lista ficam sincronizados */
  const showAppointmentCards = !loading && visibleAppointments.length > 0;

  /** Lista vertical (Ver tudo): agrupa consultas por dia, ordenadas cronologicamente */
  const appointmentsGroupedByDate = useMemo(() => {
    const sorted = [...visibleAppointments].sort(
      (a: any, b: any) =>
        dayjs(a?.resource?.start).valueOf() - dayjs(b?.resource?.start).valueOf(),
    );
    const groups = new Map<string, any[]>();
    sorted.forEach((entry: any) => {
      const key = dayjs(entry?.resource?.start).format("YYYY-MM-DD");
      const bucket = groups.get(key);
      if (bucket) bucket.push(entry);
      else groups.set(key, [entry]);
    });
    return Array.from(groups.entries());
  }, [visibleAppointments]);

  const renderMobileAppointmentCard = (
    el: any,
    index: number,
    options?: { inSlider?: boolean; onSelect?: () => void },
  ) => {
    const dependente = relatedPatient?.find(
      (e: any) => e.id === getParticipantId(el.resource, "Patient"),
    );
    const patient = patientsBundle?.find(
      (patientEl: any) =>
        patientEl.resource?.id === getParticipantId(el.resource, "Patient") &&
        patientEl?.resource?.name,
    );

    const locationEntry = locationsBundle?.filter(
      (locationEl: any) =>
        locationEl.resource?.id === getParticipantId(el.resource, "Location"),
    );

    const auxPatient =
      access?.type === "patient"
        ? patient || dependente || userPatient
        : patient;

    const inSlider = options?.inSlider ?? false;

    return (
      <ScheduleCardsMobile
        key={el.resource?.id ?? `appointment-${index}`}
        data={extractAppointmentData(el, auxPatient, locationEntry?.[0])}
        selected={selectedCardData}
        setRefetch={setRefetch}
        compact={compact}
        inSlider={inSlider}
        onClick={
          options?.onSelect ??
          (inSlider
            ? () => {
                setSelectedCard(index);
                scrollSliderToIndex(index);
              }
            : () => {})
        }
      />
    );
  };

  const handleSliderScroll = () => {
    if (isSliderScrolling.current) return;
    const el = sliderRef.current;
    if (!el || el.clientWidth <= 0) return;
    const nextIndex = Math.round(el.scrollLeft / el.clientWidth);
    if (
      nextIndex !== selectedCard &&
      nextIndex >= 0 &&
      nextIndex < visibleAppointments.length
    ) {
      setSelectedCard(nextIndex);
    }
  };

  const scrollSliderToIndex = (index: number) => {
    const el = sliderRef.current;
    if (!el || visibleAppointments.length <= 1) return;
    isSliderScrolling.current = true;
    el.scrollTo({ left: index * el.clientWidth, behavior: "smooth" });
    window.setTimeout(() => {
      isSliderScrolling.current = false;
    }, 320);
  };

  const locationReferences = appointmentsBundle
    ?.map((appointment: any) => appointment?.resource?.participant)
    .flat()
    .filter((participant: any) =>
      participant?.actor?.reference?.startsWith("Location/")
    )
    .map((participant: any) =>
      participant?.actor?.reference?.replace("Location/", "")
    )
    .filter(
      (reference: any, index: number, references: any) =>
        references.indexOf(reference) === index
    )
    .join(",");

  const selectedCardData = [
    visibleAppointments[selectedCard]?.resource,
    locationsBundle?.filter((locationEl: any) => {
      const selectedAppointment = visibleAppointments[selectedCard];
      if (selectedAppointment) {
        return (
          locationEl.resource?.id ===
          getParticipantId(selectedAppointment.resource, "Location")
        );
      }
      return false;
    })?.[0]?.resource,
  ].filter((el) => el !== undefined);

  const selectedCardTeleconsulting = useFhirData.virtualService(
    location?.find((loc: any) =>
      loc?.resource?.id.includes(selectedCardData?.[1]?.id)
    )?.resource
  );

  /**
   * Canal de atendimento do card selecionado (ex: "chat", "teleatendimento").
   * Lido da extension attendance-channel da Location correspondente.
   */
  const selectedCardAttendanceChannel = location
    ?.find((loc: any) =>
      loc?.resource?.id.includes(selectedCardData?.[1]?.id)
    )
    ?.resource?.extension?.find((ext: any) =>
      ext.url?.includes("attendance-channel")
    )?.valueCode as string | undefined;

  const virtualServiceUrl = selectedCardData?.find(
    (el: any) => el?.resourceType === "Appointment"
  )?.virtualService?.[0]?.addressUrl;

  useEffect(() => {
    setSelectedCard(0);
    if (sliderRef.current) sliderRef.current.scrollLeft = 0;
  }, [dateFilter]);

  useEffect(() => {
    setSelectedCard(0);
    if (sliderRef.current) sliderRef.current.scrollLeft = 0;
  }, [refetch]);

  /** Dashboard: mês vem do ScheduleForDash (header externo), não do picker interno. */
  useEffect(() => {
    if (hideMobileHeader) {
      setDateFieldValue(dayjs(dateFilter));
    }
  }, [dateFilter, hideMobileHeader]);

  useEffect(() => {
    if (!hideMobileHeader) {
      setDateFilter(dayjs(dateFieldValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFieldValue, hideMobileHeader]);

  useEffect(() => {
    if (access?.type === "patient")
      setIdsOrganizations(`${locationReferences}`);
  }, [locationReferences, setIdsOrganizations, access]);

  // Pares "appointmentId|slotId" dos appointments pending com slot, em string estável.
  // appointmentsBundle é recriado a cada render; depender dele fazia o efeito
  // de pagamento rodar (e mudar estado) em todo render.
  const pendingSlotIdsKey: string = (appointmentsBundle ?? [])
    .map((appointment: fhir5.BundleEntry<fhir5.Appointment>) => {
      const slotId = appointment?.resource?.slot?.[0]?.reference?.split("/")?.[1];
      if (!slotId || appointment?.resource?.status !== "pending") return "";
      return `${appointment.resource?.id}|${slotId}`;
    })
    .filter(Boolean)
    .sort()
    .join(",");

  useEffect(() => {
    if (access?.type !== "patient" || !pendingSlotIdsKey) return;

    // Só slots ainda não verificados. Marca no ref ANTES do await para não
    // chamar o mesmo slot duas vezes se o efeito rodar de novo no meio.
    const pendingPairs = pendingSlotIdsKey
      .split(",")
      .map((pair: string) => pair.split("|"))
      .filter(
        ([, slotId]: string[]) => slotId && !checkedSlotsRef.current[slotId],
      );
    if (pendingPairs.length === 0) return;
    pendingPairs.forEach(([, slotId]: string[]) => {
      checkedSlotsRef.current[slotId] = true;
    });

    const checkPaymentLinks = async () => {
      const newPaymentStatuses: { [key: string]: string } = {};

      for (const [appointmentId, slotId] of pendingPairs) {
        try {
          const paymentLink =
            await marketplaceService.getPaymentLinkBySlotId(slotId);
          newPaymentStatuses[appointmentId] = paymentLink;
        } catch (error) {
          console.error(
            `Erro ao verificar link de pagamento para slot ${slotId}:`,
            error
          );
          newPaymentStatuses[appointmentId] = "";
        }
      }

      // Atualização funcional: não depende de hasPaymentLinks no efeito.
      setHasPaymentLinks((prev) => ({ ...prev, ...newPaymentStatuses }));
    };

    checkPaymentLinks();
  }, [pendingSlotIdsKey, access?.type]);

  const renderPaymentButton = (appointmentData: any) => {
    if (
      access?.type !== "patient" ||
      appointmentData?.status !== "pending" ||
      !hasPaymentLinks[appointmentData?.id]
    ) {
      return null;
    }

    return (
      <Tooltip title="Realizar pagamento">
        <Button
          variant="contained"
          size="small"
          sx={{
            backgroundColor: "neutral600.main",
            "&:hover": {
              backgroundColor: "neutral700.main",
            },
            whiteSpace: "nowrap",
            minWidth: "fit-content",
            height: "40px",
            borderRadius: "8px",
            textTransform: "capitalize",
          }}
          onClick={() => {
            const paymentLink = hasPaymentLinks[appointmentData?.id];
            if (paymentLink) {
              window.open(paymentLink, "_blank");
            } else {
              console.error("Link de pagamento não disponível");
            }
          }}
        >
          <AttachMoney />
          <Typography variant="body2">Realizar pagamento</Typography>
        </Button>
      </Tooltip>
    );
  };

  return (
    <>
      {isMobile ? (
        <Stack
          direction="column"
          sx={{
            width: "100%",
            maxWidth: "100vw",
            height: "auto",
            padding: compact ? 0 : { xs: "12px", sm: "16px" },
            boxSizing: "border-box",
            overflow: "visible",
          }}
          gap={compact ? 1.25 : 2}
        >
          {/* Header — oculto no preview do dashboard (mês fica no título da seção) */}
          {!hideMobileHeader && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              padding: compact ? "4px 0 8px" : "12px 0",
              borderBottom: compact ? "1px solid rgba(0,0,0,0.06)" : "1px solid rgba(0,0,0,0.08)",
              gap: 1,
            }}
          >
            {!compact ? (
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{
                  color: "neutral900.main",
                  fontSize: "16px",
                }}
              >
                Consultas agendadas
              </Typography>
            ) : access?.type === "patient" ? (
              !hideNewAppointmentButton && showAppointmentCards ? (
              <Button
                size="small"
                variant="contained"
                disableElevation
                startIcon={<Add sx={{ fontSize: 16 }} />}
                onClick={() => navigate("/schedulingsearch")}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: compact ? "0.78rem" : "0.72rem",
                  py: compact ? 0.55 : 0.4,
                  px: compact ? 1.15 : 1,
                  borderRadius: 1.5,
                  minHeight: compact ? 34 : 28,
                  flexShrink: 0,
                }}
              >
                Novo agendamento
              </Button>
              ) : (
                <Box sx={{ flex: 1 }} />
              )
            ) : (
              /* Profissional no dash compacto — encaixe rápido */
              !hideNewAppointmentButton &&
              userPractitioner && hasActiveSchedule && currentContract && showAppointmentCards ? (
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  startIcon={<PersonAdd sx={{ fontSize: 16 }} />}
                  onClick={() => {
                    setCreateAppointmentDialogOpen(true);
                    setIsWalkin(true);
                  }}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: compact ? "0.78rem" : "0.72rem",
                    py: compact ? 0.55 : 0.4,
                    px: compact ? 1.15 : 1,
                    borderRadius: 1.5,
                    minHeight: compact ? 34 : 28,
                    flexShrink: 0,
                  }}
                >
                  Novo agendamento
                </Button>
              ) : (
                <Box sx={{ flex: 1 }} />
              )
            )}
            <Stack direction="row" gap={compact ? 0.75 : 1} alignItems="center" sx={{ flexShrink: 0 }}>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                  color: "neutral700.main",
                  fontSize: compact ? "14px" : "14px",
                }}
              >
                {dateFieldValue.format("MMM YYYY")}
              </Typography>
              {/* Voltar ao mês atual — visível só quando o usuário navegou para outro mês */}
              {!isCurrentMonth && (
                <Tooltip title="Voltar ao mês atual" arrow>
                  <IconButton
                    size={compact ? "small" : "medium"}
                    aria-label="Voltar ao mês atual"
                    onClick={handleResetToCurrentMonth}
                    sx={{
                      height: compact ? 34 : 36,
                      width: compact ? 34 : 36,
                      backgroundColor: "rgba(0,0,0,0.04)",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.08)",
                      },
                      "&:active": {
                        transform: "scale(0.95)",
                      },
                    }}
                  >
                    <RestartAlt
                      sx={{
                        color: "primary.main",
                        fontSize: compact ? 16 : 18,
                      }}
                    />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                size={compact ? "small" : "medium"}
                sx={{
                  height: compact ? 34 : 36,
                  width: compact ? 34 : 36,
                  backgroundColor: "rgba(0,0,0,0.04)",
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.08)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
                onClick={() => setOpenDatePickerMobile((prev) => !prev)}
              >
                <CalendarToday
                  sx={{
                    color: "neutral700.main",
                    fontSize: compact ? 16 : 18,
                  }}
                />
              </IconButton>
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="pt-br"
              >
                <MobileDatePicker
                  open={openDatePickerMobile}
                  format="MM/YYYY"
                  views={["month", "year"]}
                  onOpen={() => setOpenDatePickerMobile(true)}
                  onClose={() => setOpenDatePickerMobile(false)}
                  sx={{ display: "none" }}
                  value={dateFieldValue}
                  slotProps={{
                    dialog: {
                      fullScreen: false,
                      maxWidth: "sm",
                      PaperProps: {
                        sx: {
                          marginTop: "64px", // Espaço para o header
                          maxHeight: "calc(100vh - 64px)", // Altura máxima considerando o header
                        },
                      },
                    },
                    actionBar: {
                      actions: ["cancel", "accept"],
                    },
                  }}
                  localeText={{
                    cancelButtonLabel: "Cancelar",
                    okButtonLabel: "Confirmar",
                    toolbarTitle: "Selecionar data",
                  }}
                  onChange={(e) => setDateFieldValue(dayjs(e))}
                />
              </LocalizationProvider>
            </Stack>
          </Stack>
          )}

          {/* Consultas — slider (preview) ou lista vertical agrupada por data */}
          <Box sx={{ width: "100%", minWidth: 0 }}>
            {loading ? (
              <DataSkeleton />
            ) : showAppointmentCards ? (
              listLayout === "vertical" ? (
                <Stack spacing={2}>
                  {appointmentsGroupedByDate.map(([dateKey, dayAppointments]) => {
                    const dateLabel = dayjs(dateKey)
                      .locale("pt-br")
                      .format("dddd, DD [de] MMMM");
                    return (
                      <Box key={dateKey}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mb: 1,
                            px: 0.25,
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            color: "text.secondary",
                            textTransform: "capitalize",
                          }}
                        >
                          {dateLabel}
                        </Typography>
                        <Stack spacing={1}>
                          {dayAppointments.map((el: any, index: number) =>
                            renderMobileAppointmentCard(el, index),
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
              <>
                <Box
                  ref={sliderRef}
                  onScroll={handleSliderScroll}
                  sx={{
                    display: "flex",
                    width: "100%",
                    overflowX: visibleAppointments.length > 1 ? "auto" : "hidden",
                    overflowY: "hidden",
                    scrollSnapType:
                      visibleAppointments.length > 1 ? "x mandatory" : "none",
                    scrollBehavior: "smooth",
                    WebkitOverflowScrolling: "touch",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {visibleAppointments.map((el: any, index: number) => (
                    <Box
                      key={el.resource?.id ?? index}
                      sx={{
                        flex: "0 0 100%",
                        width: "100%",
                        maxWidth: "100%",
                        scrollSnapAlign: "start",
                        scrollSnapStop: "always",
                        boxSizing: "border-box",
                      }}
                    >
                      {renderMobileAppointmentCard(el, index, {
                        inSlider: visibleAppointments.length > 1,
                        onSelect: () => {
                          setSelectedCard(index);
                          scrollSliderToIndex(index);
                        },
                      })}
                    </Box>
                  ))}
                </Box>

                {/* Balão com bolinhas — uma preenchida por card */}
                {visibleAppointments.length > 1 && (
                  <Box sx={{ mt: compact ? 0.75 : 1 }}>
                    <ScheduleSliderDotsBalloon
                      total={visibleAppointments.length}
                      activeIndex={selectedCard}
                      onDotClick={(index) => {
                        setSelectedCard(index);
                        scrollSliderToIndex(index);
                      }}
                    />
                  </Box>
                )}

                {showViewAllBelow && onViewAll && showAppointmentCards ? (
                  <DashViewAllButton onClick={onViewAll} />
                ) : null}
              </>
              )
            ) : dashboardPreview ? (
              <Box
                sx={{
                  width: "100%",
                  py: 0.75,
                  px: 1.25,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.grey[500], 0.06),
                  border: "1px solid",
                  borderColor: alpha(theme.palette.divider, 0.6),
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    fontSize: "0.6875rem",
                    lineHeight: 1.35,
                    fontWeight: 500,
                  }}
                >
                  Nenhum agendamento encontrado
                </Typography>
              </Box>
            ) : (
              <Stack
                marginTop={compact ? 0.5 : 1}
                alignItems="center"
                spacing={compact ? 1 : 2}
                sx={{
                  width: "100%",
                  padding: compact ? "12px 8px" : "24px 12px",
                  backgroundColor: "rgba(0,0,0,0.02)",
                  borderRadius: 3,
                }}
              >
                <NoDataWarning message="Não há consultas agendadas para exibir" />
                <SecondaryButton
                  width="100%"
                  height="48px"
                  disabled={false}
                  onClick={() => navigate("/schedulingsearch")}
                >
                  Agendar uma consulta
                </SecondaryButton>
              </Stack>
            )}
          </Box>
        </Stack>
      ) : (
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          width="100%"
          sx={{ alignItems: "flex-start" }}
        >
          <Stack direction="column" width="100%" spacing={1}>
            {/* Container flexível sem altura fixa — conteúdo define a altura */}
            <Stack sx={{ minHeight: 200 }}>
              <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Typography
                  sx={{ color: "neutral900.main" }}
                  variant="subtitle1"
                  fontWeight={700}
                >
                  Consultas agendadas
                </Typography>
              </Stack>
              {openDatePicker && (
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="pt-br"
                >
                  <DateCalendar
                    sx={{
                      position: "absolute",
                      backgroundColor: "neutral0.main",
                      zIndex: "1000",
                      marginLeft: { lg: "110px", xs: "0px" },
                    }}
                    value={dateFilter}
                    onMonthChange={(e) => {
                      setFilterType("day");
                    }}
                    onChange={(e) => {
                      if (e) setDateFilter(e);
                      setFilterType("day");
                      setOpenDatePicker(false);
                    }}
                    minDate={dayjs()}
                  />
                </LocalizationProvider>
              )}
              <DateFilter
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                filterType={filterType}
                setFilterType={setFilterType}
              />
              <Stack
                sx={{
                  overflow: "auto",
                  maxHeight: access?.type === "patient" ? "50vh" : "45vh",
                  pr: 0.5,
                  mb: 1,
                  "&::-webkit-scrollbar": { width: "4px" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "primary700.main",
                    borderRadius: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                {loading ? (
                  <DataSkeleton />
                ) : showAppointmentCards ? (
                  visibleAppointments.map((el: any, index: number) => {
                    const dependente = relatedPatient?.find(
                      (e: any) =>
                        e.id === getParticipantId(el.resource, "Patient")
                    );
                    const patient = patientsBundle?.find(
                      (patientEl: any) =>
                        patientEl.resource?.id ===
                          getParticipantId(el.resource, "Patient") &&
                        patientEl?.resource?.name
                    );

                    const locationId = locationsBundle?.filter(
                      (locationEl: any) =>
                        locationEl.resource?.id ===
                        getParticipantId(el.resource, "Location")
                    );

                    // Localização completa com extensions para este agendamento
                    const cardLocationResource = location?.find((loc: any) =>
                      loc?.resource?.id.includes(
                        locationId?.[0]?.resource?.id
                      )
                    )?.resource;

                    const locationTeleconsulting =
                      useFhirData.virtualService(cardLocationResource);

                    /**
                     * Canal de atendimento desta agenda (ex: "chat", "teleatendimento").
                     * Lido da extension attendance-channel da Location do card.
                     */
                    const cardAttendanceChannel =
                      cardLocationResource?.extension?.find((ext: any) =>
                        ext.url?.includes("attendance-channel")
                      )?.valueCode as string | undefined;

                    const auxPatient =
                      access?.type === "patient"
                        ? patient || dependente || userPatient
                        : patient;

                    const appointmentData = extractAppointmentData(
                      el,
                      auxPatient,
                      location?.[0]
                    );

                    const hideTeleconsultForPayment =
                      access?.type === "patient" &&
                      appointmentData?.status === "pending" &&
                      !!hasPaymentLinks[appointmentData?.id];

                    return (
                      <CardSchedule
                        key={index}
                        data={appointmentData}
                        locationTeleconsulting={locationTeleconsulting}
                        locationAttendanceChannel={cardAttendanceChannel}
                        hideTeleconsultBecausePaymentPending={
                          hideTeleconsultForPayment
                        }
                        selected={selectedCard}
                        setOpenAppointmentDetails={setOpenAppointmentDetails}
                        index={index}
                        onClick={() => {
                          setSelectedCard(index);
                        }}
                      />
                    );
                  })
                ) : (
                  <>
                    {access?.type === "patient" ? (
                      <Stack alignItems="center" spacing={2}>
                        <NoDataWarning message="Não há consultas agendadas para exibir" />
                        <SecondaryButton
                          width="auto"
                          height="50px"
                          disabled={false}
                          onClick={() => navigate("/schedulingsearch")}
                        >
                          Agendar uma consulta
                        </SecondaryButton>
                      </Stack>
                    ) : (
                      <>
                        <NoDataWarning />
                      </>
                    )}
                  </>
                )}
              </Stack>

              {/* Ações para pacientes - Apenas em desktop (não exibidas em mobile) */}
              {access?.type === "patient" && !isMobile && (
                <Stack
                  direction="column"
                  spacing={2}
                  justifyContent="flex-end"
                  mt={1}
                >
                  {selectedCardData?.[0]?.id &&
                    renderPaymentButton(selectedCardData[0])}
                  {selectedCardData?.[0]?.id &&
                    selectedCardTeleconsulting &&
                    !(
                      selectedCardData[0].status === "pending" &&
                      !!hasPaymentLinks[selectedCardData[0].id]
                    ) && (
                    <VideoConferencia
                      roomUrl={virtualServiceUrl}
                      attendanceType={selectedCardAttendanceChannel}
                      appointmentData={{
                        id: selectedCardData[0].id,
                        patientName: selectedCardData[0].patientName,
                        practitionerName: selectedCardData[0].practitioner,
                        startTime: selectedCardData[0].startTime,
                      }}
                    />
                  )}
                </Stack>
              )}
            </Stack>

            <Stack spacing={1}>
              {userPractitioner ? (
                <Stack spacing={1}>
                  {currentContract ? (
                    <>
                      {hasActiveSchedule ? (
                        <>
                          <Button
                            sx={{
                              gap: 1,
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: 3,
                              fontSize: 16,
                              textTransform: "none",
                              color: "neutral0.main",
                              backgroundColor: "primary700.main",
                              ":hover": {
                                backgroundColor: "primary800.main",
                              },
                            }}
                            onClick={() => {
                              setCreateAppointmentDialogOpen(true);
                              setIsWalkin(true);
                            }}
                          >
                            <PersonAdd /> Realizar encaixe
                          </Button>
                        </>
                      ) : (
                        <SecondaryButton
                          width="auto"
                          height="50px"
                          disabled={false}
                          onClick={() => navigate("/newschedule")}
                        >
                          Crie uma agenda
                        </SecondaryButton>
                      )}
                    </>
                  ) : (
                    <></>
                  )}
                </Stack>
              ) : (
                <Stack height="38px" />
              )}
              {createAppointmentDialogOpen && (
                <CreateAppointmentDialog
                  isOpen={createAppointmentDialogOpen}
                  setModalOpen={setCreateAppointmentDialogOpen}
                  setRefetchDashboard={setRefetch}
                  cpfT={""}
                  walkin={isWalkin}
                  isShortCutAppointment={isShortCutAppointment}
                />
              )}
            </Stack>
          </Stack>
          {access.type === "professional" ? (
            <Stack direction="column" width="100%" justifyContent="center">
              <AppointmentDetails
                data={selectedCardData}
                patient={patientsBundle?.find(
                  (patientEl: fhir5.BundleEntry<fhir5.Patient> | undefined) =>
                    patientEl?.resource?.id ===
                      getParticipantId(
                        visibleAppointments[selectedCard]?.resource,
                        "Patient",
                      ) && patientEl?.resource?.name
                )}
                setRefetch={setRefetch}
              />
            </Stack>
          ) : (
            <CommonDialog
              isOpen={openAppointmentDetails}
              handleClose={() => setOpenAppointmentDetails(false)}
            >
              <Stack mt={3}>
                <AppointmentDetails
                  data={selectedCardData}
                  patient={patientsBundle?.find(
                    (patientEl: fhir5.BundleEntry<fhir5.Patient> | undefined) =>
                      patientEl?.resource?.id ===
                        getParticipantId(
                          visibleAppointments[selectedCard]?.resource,
                          "Patient",
                        ) && patientEl?.resource?.name
                  )}
                  setRefetch={setRefetch}
                />
              </Stack>
            </CommonDialog>
          )}
        </Stack>
      )}
    </>
  );
}
